import { RedirectToSignIn, Show, useClerk, OrganizationSwitcher } from '@clerk/react';
import {
  Box,
  Button,
  Grid,
  HeaderV2,
  Menu,
  ErrorBoundary,
  TextColor,
  BackgroundColor,
  Typography,
} from '@brainpool/components';
import { Avatar, Close, Help, Login, Menu as MenuIcon } from '@brainpool/icon-pack';
import { TuneddRoutes } from '@brainpool/tunedd-lib';
import { useHeaderMenu } from './layoutProps';
import { ProductFruits } from 'react-product-fruits';
import { useApolloClient } from '@apollo/client/react';
import { useReactiveVar } from '@apollo/client/react';
import { OrganizationProps, reactiveVars, UserDetailsProps } from '@brainpool/apollo-connect';
import { useEffect, useState } from 'react';
import { CustomThemeProvider } from '@brainpool/themes';
import { WebsocketService } from '@brainpool/tunedd-lib/services/websocketService';
import { LoggerProvider, loggerService, useLogger } from '@brainpool/logger';
import { initializeWebSocketHelpers } from '@brainpool/tunedd-lib/services/websocketHelperFunctions';

interface AppProps {
  productFruitsKey: string;
}

export function App({ productFruitsKey }: AppProps) {
  const apolloClient = useApolloClient();
  const websocketUrl = useReactiveVar(reactiveVars.websocketUrl);
  const token = useReactiveVar(reactiveVars.token);
  const { signOut } = useClerk();

  const [openMenu, setOpenMenu] = useState(false);

  const userDetails = useReactiveVar<UserDetailsProps | null>(reactiveVars.userDetails);
  const org = useReactiveVar<OrganizationProps | null>(reactiveVars.organization);
  const headerMenu = useHeaderMenu();

  function LoggerInitializer() {
    const logger = useLogger();

    useEffect(() => {
      loggerService.setLogger(logger);
    }, [logger]);
    return null;
  }

  useEffect(() => {
    console.log(`App version: ${__APP_VERSION__}`);
  }, []);

  useEffect(() => {
    if (apolloClient) {
      initializeWebSocketHelpers(apolloClient);
    }
  }, [apolloClient]);

  useEffect(() => {
    if (org && websocketUrl && token && apolloClient) {
      const wsService = WebsocketService.getInstance();
      wsService.setApolloClient(apolloClient);
      wsService.createWebSocketClient(websocketUrl as string, token as string);
    } else {
      loggerService.log({
        message: 'WebSocket service not initialized due to missing parameters',
        logLevel: 'warning',
        context: { action: 'App useEffect', websocketUrl, token },
      });
    }
  }, [websocketUrl, token, apolloClient, org]);

  //build the app
  return (
    <CustomThemeProvider theme="tunedd">
      <LoggerProvider
        appContext={{ appName: 'Tunedd app', user: userDetails, org }}
        newRelicEnabled={true}
      >
        {userDetails && (
          <ErrorBoundary>
            <ProductFruits
              workspaceCode={productFruitsKey}
              language="en"
              user={{
                username: userDetails?.userId ?? '',
                email: userDetails?.email,
                firstname: userDetails?.firstName,
                lastname: userDetails?.lastName,
                props: {
                  orgId: userDetails?.orgId,
                },
              }}
            />
          </ErrorBoundary>
        )}

        <Box sx={{ backgroundColor: BackgroundColor.black, position: 'relative', zIndex: 2000 }}>
          <HeaderV2
            position="relative"
            data-testid="header"
            logo={
              <Typography variant="h3" fontWeight={400} sx={{ mb: 0 }}>
                tunedd
              </Typography>
            }
            textColor={TextColor.white}
            backgroundColor={BackgroundColor.black}
            shadow={false}
            drawer={true}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            customMenuIcon={<MenuIcon fill="white" />}
            customMenuCloseIcon={<Close fill="white" />}
            menu={<Menu {...headerMenu} onClick={setOpenMenu} />}
            children={
              <>
                <Button
                  onClick={() => {
                    window.location.href = 'mailto:contact@tunedd.ai?subject=Support request';
                  }}
                  sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
                >
                  <Typography
                    fontFamily="Fustat, Arial, sans-serif"
                    fontWeight={700}
                    sx={{ mb: 0, px: 1, color: 'white' }}
                  >
                    Request Help
                  </Typography>
                  <Help fill="white" />
                </Button>
                <Box sx={{ border: 0, borderRight: '1px solid white', height: 16 }} />
                <Button
                  onClick={() => {
                    const trigger = document.querySelector(
                      '.cl-organizationSwitcherTrigger'
                    ) as HTMLElement;
                    trigger?.click();
                  }}
                  sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
                >
                  <Typography
                    fontFamily="Fustat, Arial, sans-serif"
                    fontWeight={700}
                    sx={{ mb: 0, px: 1, color: 'white' }}
                  >
                    My Account
                  </Typography>
                  <Avatar fill="white" />
                </Button>
                <Box
                  sx={{
                    position: 'absolute',
                    right: 200,
                    width: 0,
                    height: 0,
                    overflow: 'hidden',
                  }}
                >
                  <OrganizationSwitcher hidePersonal={true} />
                </Box>
                <Box sx={{ border: 0, borderRight: '1px solid white', height: 16 }} />
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => signOut({ redirectUrl: '/' })}
                >
                  <Typography
                    fontFamily="Fustat, Arial, sans-serif"
                    fontWeight={700}
                    sx={{ px: 1 }}
                  >
                    Sign Out
                  </Typography>
                  <Login fill="white" />
                </Button>
              </>
            }
          />
        </Box>
        <Show when="signed-out">
          <RedirectToSignIn />
        </Show>
        <Show when="signed-in">
          <Box sx={{ p: 0 }} data-testid="content">
            <Grid container gap={2}>
              <TuneddRoutes />
            </Grid>
          </Box>
        </Show>
        <LoggerInitializer />
      </LoggerProvider>
    </CustomThemeProvider>
  );
}

export default App;
